import { action, computed, observable, runInAction } from 'mobx';

import { defaultRegistroImpuestoPredial } from './constants';

import { aperturaStore, AperturaStore } from './aperturaStore';
import { requestStore } from './requestStore';

import {
    Comentario,
    ImpuestosPredialCuentas,
    ImpuestosPredialDescuentos,
    ImpuestosPredialIngresos,
    PredialInformacion,
    RegistroImpuestosEstado,
    RegistroImpuestosPredial
} from '../models';

export class RegistroImpuestosPredialStore {
    @observable
    isSubmitting = false;

    @observable
    registroImpuestos: RegistroImpuestosPredial = { ...defaultRegistroImpuestoPredial };

    @observable
    defaultRegistroImpuestos: RegistroImpuestosPredial = { ...defaultRegistroImpuestoPredial };

    @observable
    isSavingCuentas = false;

    @observable
    municipioId: number = 0;

    @observable
    newComment: string = '';

    @observable
    mode: 'captura' | 'aprobaciones' = 'captura';

    @observable
    isDirty = false;

    @observable
    isLoading = true;

    @computed
    get isReadonly(): boolean {
        const { estado } = this.registroImpuestos;
        return (
            this.mode === 'aprobaciones' ||
            estado === RegistroImpuestosEstado.Capturado ||
            estado === RegistroImpuestosEstado.Aprobado ||
            estado === RegistroImpuestosEstado.Corregido
        );
    }

    @computed
    get canSubmit(): boolean {
        const { ingresos, cuentas, descuentos } = this.registroImpuestos;
        return ingresos.isSaved && cuentas.isSaved && descuentos.isSaved;
    }

    @computed
    get apertura(): AperturaStore {
        return aperturaStore;
    }

    @computed
    get ingresos(): ImpuestosPredialIngresos {
        return this.registroImpuestos.ingresos;
    }

    @computed
    get cuentas(): ImpuestosPredialCuentas {
        return this.registroImpuestos.cuentas;
    }

    @computed
    get descuentos(): ImpuestosPredialDescuentos {
        return this.registroImpuestos.descuentos;
    }

    @action
    updateNewComment(newComment: string) {
        this.newComment = newComment;
    }

    @action
    setMode = (mode: 'captura' | 'aprobaciones') => {
        this.mode = mode;
    };

    @action
    setMunicipioId(municipioId: number) {
        this.municipioId = municipioId;
    }

    @action
    async getForm() {
        this.isLoading = true;

        const formResponse = await requestStore.getForm('predial', this.municipioId);

        runInAction(() => {
            this.isLoading = false;
            this.isDirty = false;
        });

        if (!formResponse) {
            return;
        }

        Object.assign(this.registroImpuestos, formResponse.registroImpuestosPredial);
        this.apertura.saveApertura(formResponse.apertura);
    }

    @action
    async getNewForm() {
        const formResponse = await requestStore.fetchNewForm('predial', this.municipioId);

        if (!formResponse) {
            return null;
        }

        this.registroImpuestos.id = formResponse.id;
        Object.assign(this.defaultRegistroImpuestos, formResponse);
    }

    @action
    async saveForm(subform: PredialInformacion, formNumber: number) {
        const { registroImpuestos } = this;

        const resultado = await requestStore.saveForm(
            subform,
            registroImpuestos.id,
            'predial',
            formNumber
        );

        if (resultado === 'success') {
            if (registroImpuestos.estado !== 'Rechazado') {
                registroImpuestos.estado = RegistroImpuestosEstado.Guardado;
                subform.isSaved = true;
            }
            return 'success';
        }
        return 'failure';
    }

    @action
    submitForm = async () => {
        const { id } = this.registroImpuestos;
        this.isSubmitting = true;

        const response = await requestStore.submitForm(id, 'predial', this.newComment);
        const success = response !== null && response !== 'failure';
        const corregido = this.registroImpuestos.estado === RegistroImpuestosEstado.Rechazado;

        if (success) {
            this.registroImpuestos.estado = corregido
                ? RegistroImpuestosEstado.Corregido
                : RegistroImpuestosEstado.Capturado;
            this.registroImpuestos.comentarios = (response as Comentario).comentario;
        }
        this.isSubmitting = false;
    };

    markDirty = () => (this.isDirty = true);

    unmarkDirty = () => (this.isDirty = false);
}

export const registroImpuestosPredialStore = new RegistroImpuestosPredialStore();
